import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  ChartBarIcon,
  MapPinIcon,
  CloudArrowUpIcon,
  ArrowPathIcon,
  BeakerIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../../components/animations/FadeIn';
import { concentrationApi } from '../../api/concentration';
import { fetchV32PortfolioCostSummary, type V32PortfolioCostSummary } from '../../api/polygon';
import { PortfolioSiteMap, MapSite } from '../../components/polygon/PortfolioSiteMap';
import { sitesApi } from '../../api/sites';
import { Sparkline } from '../data-vault/components/ui/Sparkline';
import type { HybridRankingResponse } from '@/types';

// Animated counter component
function Counter({ end, decimals = 0, prefix = '', suffix = '' }: { end: number; decimals?: number; prefix?: string; suffix?: string }) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end]);

  return (
    <span>
      {prefix}
      {count.toFixed(decimals)}
      {suffix}
    </span>
  );
}

// Priority category styles (hybrid score based)
const PRIORITY_STYLES: Record<string, { bg: string; text: string; label: string; border: string; color: string }> = {
  critical: { bg: 'bg-red-500/20',    text: 'text-red-400',    label: 'Critical', border: 'border-red-500/40', color: '#ef4444' },
  high:     { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'High',     border: 'border-orange-500/40', color: '#f97316' },
  moderate: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Moderate', border: 'border-yellow-500/40', color: '#eab308' },
  low:      { bg: 'bg-green-500/20',  text: 'text-green-400',  label: 'Low',      border: 'border-green-500/40', color: '#22c55e' },
};

const TREND_ICONS: Record<string, { icon: typeof ArrowTrendingUpIcon; color: string; label: string }> = {
  worsening:  { icon: ArrowTrendingUpIcon,   color: 'text-mono-300', label: 'Worsening' },
  stable:     { icon: ChartBarIcon,          color: 'text-mono-500', label: 'Stable' },
  mixed:      { icon: ChartBarIcon,          color: 'text-mono-400', label: 'Mixed' },
  improving:  { icon: ArrowTrendingDownIcon, color: 'text-mono-600', label: 'Improving' },
};

function formatCurrency(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

export function PolygonDashboard() {
  const [portfolio, setPortfolio] = useState<HybridRankingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [allSites, setAllSites] = useState<MapSite[]>([]);
  const [v32Portfolio, setV32Portfolio] = useState<V32PortfolioCostSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchV32PortfolioCostSummary()
      .then(resp => { if (!cancelled) setV32Portfolio(resp); })
      .catch(err => {
        if (cancelled) return;
        console.warn('[POLYGON] v3.2 portfolio cost summary fetch failed:', err);
        setV32Portfolio(null);
      });
    return () => { cancelled = true; };
  }, []);

  const fetchPortfolio = useCallback(async () => {
    try {
      setLoading(true);
      const data = await concentrationApi.getHybridRanking();
      setPortfolio(data);
    } catch (err) {
      console.error('Failed to fetch hybrid ranking:', err);
      setPortfolio(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
    // Fetch all sites across pages for the map
    (async () => {
      try {
        const pageSize = 100;
        const first = await sitesApi.list({ page: 1, page_size: pageSize });
        let all = first.items || [];
        const totalPages = first.pages || 1;
        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => sitesApi.list({ page: i + 2, page_size: pageSize }))
          );
          for (const p of remaining) {
            all = all.concat(p.items || []);
          }
        }
        setAllSites(all.map((s) => ({
          id: s.id,
          name: s.name,
          latitude: s.latitude,
          longitude: s.longitude,
          threat_tier: s.threat_tier ?? undefined,
          primary_contaminant: s.primary_contaminant ?? null,
          aquifer: s.aquifer_type ?? null,
        })));
      } catch (err) {
        console.error('[PolygonDashboard] Failed to fetch sites for map:', err);
      }
    })();
  }, [fetchPortfolio]);

  const sites = portfolio?.sites ?? [];

  // Merge portfolio v3.2 attributes (site_trend, priority_category, plus the
  // new long-horizon aggregates) into the map sites by id so the filter bar
  // and dot colors can use the inference output directly.
  const enrichedSites = useMemo<MapSite[]>(() => {
    if (!sites.length) return allSites;
    const byId = new Map(sites.map((s) => [s.site_id, s]));
    return allSites.map((s) => {
      const p = byId.get(s.id);
      if (!p) return s;
      return {
        ...s,
        site_trend: p.site_trend ?? s.site_trend ?? null,
        priority_category: p.priority_category ?? s.priority_category ?? null,
        // v3.2 fields — undefined on the API response until inference runs
        // post-migration, in which case the dot stays on the static tier.
        tier_v32: p.tier_v32 ?? null,
        tier_confidence: p.tier_confidence ?? null,
        concern_horizon_label: p.concern_horizon_label ?? null,
        trajectory_shape: p.trajectory_shape ?? null,
      };
    });
  }, [allSites, sites]);

  // Compute stats in a single memoized pass.
  // Every count here is v3.2-aware: a site is "critical" because it still has
  // wells exceeding at 20yr, not because it has wells exceeding today. Same
  // for "worsening" — we trust trajectory_shape over the 3mo site_trend.
  const dashboardStats = useMemo(() => {
    if (sites.length === 0) return null;
    const avgHybridScore = sites.reduce((sum, s) => sum + s.hybrid_score, 0) / sites.length;

    // "Wells above MCL" — worst-case future count, not today. Sites without
    // v3.2 yet fall back to the 3mo count.
    const totalWellsExceeding = sites.reduce(
      (sum, s) => sum + (s.wells_exceeding_furthest ?? s.wells_exceeding_mcl),
      0,
    );
    const totalWells = sites.reduce((sum, s) => sum + s.total_wells, 0);

    // Average burn rate across sites that are still active concerns under
    // v3.2 (any v3.2 tier ≤ 3 OR — when v3.2 hasn't run — priority not 'low').
    // Sites that v3.2 says will be clean by 5yr are excluded from the burn-
    // rate average since their cost will fall off naturally.
    const activeSites = sites.filter(s =>
      s.tier_v32 != null
        ? s.tier_v32 <= 3
        : s.priority_category !== 'low',
    );
    const avgBurnRate = activeSites.length
      ? activeSites.reduce((sum, s) => sum + s.monthly_burn_rate, 0) / activeSites.length
      : 0;

    const criticalSites: typeof sites = [];
    const highSites: typeof sites = [];
    const moderateSites: typeof sites = [];
    const lowSites: typeof sites = [];
    const worseningSites: typeof sites = [];
    const improvingSites: typeof sites = [];

    for (const s of sites) {
      // Bucket by v3.2 tier when present; otherwise legacy priority_category.
      const bucket = s.tier_v32 != null
        ? (s.tier_v32 === 1 ? 'critical'
          : s.tier_v32 === 2 ? 'high'
          : s.tier_v32 === 3 ? 'moderate'
          : 'low')
        : s.priority_category;
      if (bucket === 'critical') criticalSites.push(s);
      else if (bucket === 'high') highSites.push(s);
      else if (bucket === 'moderate') moderateSites.push(s);
      else lowSites.push(s);

      // Trend bucket — prefer v3.2 trajectory_shape over the 3mo site_trend.
      const shape = s.trajectory_shape;
      if (shape === 'worsening' || (shape == null && s.site_trend === 'worsening')) {
        worseningSites.push(s);
      } else if (shape === 'converging' || (shape == null && s.site_trend === 'improving')) {
        improvingSites.push(s);
      }
    }

    // "Attention required" = critical + high (v3.2 T1 + T2).
    const attentionSites = [...criticalSites, ...highSites];

    return {
      avgHybridScore,
      criticalSites,
      highSites,
      moderateSites,
      lowSites,
      attentionSites,
      worseningSites,
      improvingSites,
      totalWellsExceeding,
      totalWells,
      avgBurnRate,
    };
  }, [sites]);

  // Pre-compute priority category counts. Buckets follow the same v3.2-aware
  // mapping as dashboardStats so the priority-distribution bars match the
  // "critical sites" headline number.
  const categoryCounts = useMemo(() => {
    const counts: Record<string, typeof sites> = {};
    for (const s of sites) {
      const cat = s.tier_v32 != null
        ? (s.tier_v32 === 1 ? 'critical'
          : s.tier_v32 === 2 ? 'high'
          : s.tier_v32 === 3 ? 'moderate'
          : 'low')
        : (s.priority_category || 'unknown');
      if (!counts[cat]) counts[cat] = [];
      counts[cat].push(s);
    }
    return counts;
  }, [sites]);

  // Pre-compute trend counts
  const trendCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of sites) {
      const trend = s.site_trend || 'unknown';
      counts[trend] = (counts[trend] || 0) + 1;
    }
    return counts;
  }, [sites]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <ArrowPathIcon className="h-8 w-8 text-mono-500 animate-spin mx-auto mb-4" />
          <p className="text-mono-500 text-sm tracking-wider">LOADING PORTFOLIO...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!portfolio || portfolio.total_sites === 0) {
    return (
      <div className="space-y-6">
        <FadeIn>
          <div>
            <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">PORTFOLIO OVERVIEW</h1>
            <p className="text-mono-500 text-sm mt-1">Site condition analysis across your portfolio</p>
          </div>
        </FadeIn>
        <FadeIn delay={100}>
          <div className="flex flex-col items-center justify-center py-20 bg-mono-900 border border-mono-800 rounded-lg">
            <CloudArrowUpIcon className="h-16 w-16 text-mono-600 mb-4" />
            <h2 className="text-xl font-semibold text-mono-200 tracking-wider mb-2 font-orbitron">NO PREDICTIONS AVAILABLE</h2>
            <p className="text-mono-500 text-sm text-center max-w-md mb-6">
              Upload environmental site data and run predictions to see site condition scores across your portfolio.
            </p>
            <Link
              to="/polygon/upload"
              className="flex items-center px-6 py-3 bg-mono-200 hover:bg-mono-100 text-mono-900 text-xs tracking-widest transition-colors"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-2" />
              UPLOAD SITE
            </Link>
          </div>
        </FadeIn>
      </div>
    );
  }

  const {
    avgHybridScore,
    attentionSites,
    criticalSites,
    highSites,
    worseningSites,
    improvingSites,
    totalWellsExceeding,
    totalWells,
    avgBurnRate,
  } = dashboardStats!;

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">PORTFOLIO OVERVIEW</h1>
            <p className="text-mono-500 text-sm mt-1">
              Site condition analysis across {portfolio.total_sites} sites
            </p>
          </div>
          <Link
            to="/polygon/upload"
            className="flex items-center px-4 py-2 bg-mono-200 hover:bg-mono-100 text-mono-900 text-xs tracking-widest transition-colors"
          >
            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
            UPLOAD SITE
          </Link>
        </div>
      </FadeIn>

      {/* Alert Banner - Critical/High Priority Sites */}
      {attentionSites.length > 0 && (
        <FadeIn delay={100}>
          <div className="bg-mono-900 border border-mono-600 rounded-lg p-6">
            <div className="flex items-start">
              <ShieldExclamationIcon className="h-8 w-8 text-mono-300 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-mono-100 tracking-wider font-orbitron">ATTENTION REQUIRED</h2>
                <p className="text-mono-400 text-sm mt-1">
                  {attentionSites.length} site{attentionSites.length !== 1 ? 's' : ''} scored Critical or High priority.
                  {worseningSites.length > 0 && ` ${worseningSites.length} site${worseningSites.length !== 1 ? 's are' : ' is'} trending worse.`}
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {attentionSites.slice(0, 6).map((s) => {
                    const style = PRIORITY_STYLES[s.priority_category] || PRIORITY_STYLES.moderate;
                    return (
                      <Link
                        key={s.site_id}
                        to={`/polygon/sites/${s.site_id}`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded border ${style.bg} ${style.border} hover:opacity-80 transition-opacity`}
                      >
                        <span className={`text-sm font-bold ${style.text}`}>{s.hybrid_score.toFixed(0)}</span>
                        <span className="text-mono-300 text-xs">{s.site_name}</span>
                      </Link>
                    );
                  })}
                </div>
                <div className="grid grid-cols-3 gap-6 mt-4">
                  <div>
                    <div className="text-3xl font-bold text-mono-100 font-orbitron">
                      {criticalSites.length}
                    </div>
                    <div className="text-xs text-mono-500 tracking-wider">CRITICAL SITES</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-mono-200 font-orbitron">
                      {worseningSites.length}
                    </div>
                    <div className="text-xs text-mono-500 tracking-wider">WORSENING TREND</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-mono-300 font-orbitron">
                      <Counter end={totalWellsExceeding} />
                    </div>
                    <div className="text-xs text-mono-500 tracking-wider">WELLS ABOVE MCL · WORST FUTURE</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      )}

      {/* Site Map */}
      <FadeIn delay={150}>
        <PortfolioSiteMap sites={enrichedSites} />
      </FadeIn>

      {/* Main Stats */}
      <StaggeredFadeIn className="grid grid-cols-1 md:grid-cols-4 gap-4" staggerDelay={100}>
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-mono-500 text-xs tracking-wider">AVG PRIORITY SCORE</span>
            <ChartBarIcon className="h-5 w-5 text-mono-500" />
          </div>
          <div className="text-2xl font-bold text-mono-100 font-orbitron">
            <Counter end={avgHybridScore} decimals={1} />
          </div>
          <div className="text-xs text-mono-500 mt-1">out of 100</div>
        </div>

        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-mono-500 text-xs tracking-wider">SITES ASSESSED</span>
            <MapPinIcon className="h-5 w-5 text-mono-500" />
          </div>
          <div className="text-2xl font-bold text-mono-100 font-orbitron">
            <Counter end={portfolio.total_sites} />
          </div>
          <div className="text-xs text-mono-500 mt-1">{totalWells} wells monitored</div>
        </div>

        <div className="bg-mono-900 border border-red-500/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-mono-500 text-xs tracking-wider">PORTFOLIO 20YR LIABILITY</span>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          </div>
          <div className="text-2xl font-bold text-mono-100 font-orbitron">
            {formatCurrency(v32Portfolio?.total_liability ?? 0)}
          </div>
          <div className="text-xs text-mono-400 mt-1">
            {v32Portfolio
              ? `${v32Portfolio.total_sites} site${v32Portfolio.total_sites === 1 ? '' : 's'} · ${formatCurrency(v32Portfolio.total_cost_of_delay)} cost-of-delay`
              : 'computing…'}
          </div>
        </div>

        <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-mono-500 text-xs tracking-wider">WELLS ABOVE MCL · WORST FUTURE</span>
            <BeakerIcon className="h-5 w-5 text-mono-400" />
          </div>
          <div className="text-2xl font-bold text-mono-100 font-orbitron">
            <Counter end={totalWellsExceeding} />
          </div>
          <div className="text-xs text-mono-400 mt-1">
            {totalWells > 0 ? ((totalWellsExceeding / totalWells) * 100).toFixed(0) : 0}% at furthest exceeding horizon
          </div>
        </div>
      </StaggeredFadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Risk Distribution */}
        <FadeIn delay={300} className="lg:col-span-1">
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 h-full">
            <h3 className="text-sm font-semibold text-mono-100 tracking-wider mb-4 font-orbitron">PRIORITY DISTRIBUTION</h3>
            <div className="space-y-4">
              {(['critical', 'high', 'moderate', 'low'] as const).map((category) => {
                const style = PRIORITY_STYLES[category];
                const count = (categoryCounts[category] || []).length;
                return (
                  <Link
                    key={category}
                    to={`/polygon/sites?priority=${category}`}
                    className="block group"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center">
                        <span
                          className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold"
                          style={{ backgroundColor: style.color + '33', color: style.color }}
                        >
                          {category.toUpperCase()}
                        </span>
                        <span className="ml-2 text-mono-200 group-hover:text-mono-100 transition-colors text-sm">
                          {style.label}
                        </span>
                      </div>
                      <span className="text-mono-500 text-sm">{count} sites</span>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-1 h-2 bg-mono-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${portfolio.total_sites > 0 ? (count / portfolio.total_sites) * 100 : 0}%`, backgroundColor: style.color }}
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-mono-800">
              <h4 className="text-xs text-mono-400 tracking-wider mb-3">TREND BREAKDOWN</h4>
              <div className="space-y-2">
                {(['worsening', 'mixed', 'stable', 'improving'] as const).map((trend) => {
                  const info = TREND_ICONS[trend];
                  const count = trendCounts[trend] || 0;
                  const TrendIcon = info.icon;
                  return (
                    <div key={trend} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendIcon className={`h-4 w-4 ${info.color}`} />
                        <span className="text-mono-400 text-sm">{info.label}</span>
                      </div>
                      <span className="text-mono-500 text-sm">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Highest Priority Sites */}
        <FadeIn delay={400} className="lg:col-span-2">
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-mono-100 tracking-wider font-orbitron">HIGHEST PRIORITY SITES</h3>
              <Link to="/polygon/sites?sort=priority" className="text-mono-400 hover:text-mono-200 text-xs tracking-wider">
                VIEW ALL
              </Link>
            </div>

            {sites.length > 0 ? (
              <div className="space-y-3">
                {sites.slice(0, 8).map((site, index) => {
                  const prioStyle = PRIORITY_STYLES[site.priority_category] || PRIORITY_STYLES.moderate;
                  const trendInfo = TREND_ICONS[site.site_trend] || TREND_ICONS.stable;
                  const TrendIcon = trendInfo.icon;

                  return (
                    <Link
                      key={site.site_id}
                      to={`/polygon/sites/${site.site_id}`}
                      className="block bg-mono-950/50 hover:bg-mono-800/50 rounded-lg p-4 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-mono-600 text-sm w-6">{index + 1}.</span>
                          <div
                            className="flex items-center justify-center w-10 h-10 rounded-lg"
                            style={{ backgroundColor: prioStyle.color + '33' }}
                          >
                            <span className="text-sm font-bold" style={{ color: prioStyle.color }}>
                              {site.hybrid_score.toFixed(0)}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="flex items-center gap-2">
                              <span className="text-mono-200 group-hover:text-mono-100 transition-colors">{site.site_name}</span>
                              <TrendIcon className={`h-4 w-4 ${trendInfo.color}`} />
                            </div>
                            <div className="text-xs text-mono-500 mt-0.5">
                              {/* Prefer v3.2 "wells exceeding at furthest horizon" — that's
                                  what drives the ranking. Fall back to 3mo count when v3.2
                                  hasn't run yet. */}
                              {(site.wells_exceeding_furthest ?? site.wells_exceeding_mcl)} of {site.total_wells} wells exceed
                              {site.concern_horizon_label
                                ? ` at ${site.concern_horizon_label}`
                                : ''}
                              {site.monthly_burn_rate > 0 && ` · ${formatCurrency(site.monthly_burn_rate)}/mo`}
                              {site.closure_median_quarters != null && (
                                <span className="ml-1">
                                  · Closure: ~{site.closure_median_quarters <= 4
                                    ? `${Math.round(site.closure_median_quarters * 3)}mo`
                                    : `${(site.closure_median_quarters / 4).toFixed(1)}yr`}
                                  {site.closure_probability_10yr != null && (
                                    <span className={site.closure_probability_10yr > 0.5 ? 'text-emerald-500' : 'text-amber-500'}>
                                      {' '}({Math.round(site.closure_probability_10yr * 100)}% 10yr)
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-3">
                          {/* v3.2 future-only avg(conc/MCL) trajectory — split
                              per analyte group. BTEX → 20yr (6 points), TPH →
                              2yr (3 points). Each reaches its native max
                              horizon so the visual time-axis is honest. */}
                          {(() => {
                            const cs = v32Portfolio?.sites.find(s => s.site_id === site.site_id);
                            if (!cs) return null;
                            const renderTraj = (
                              label: string,
                              traj: { horizons: string[]; ratios: (number | null)[] } | undefined,
                              color: string,
                            ) => {
                              const ratios = (traj?.ratios ?? []).filter((v): v is number => typeof v === 'number');
                              if (ratios.length < 2) return null;
                              const tooltip = (traj?.ratios ?? [])
                                .map((r, i) => r != null ? `${traj?.horizons[i] ?? `h${i}`}: ${r.toFixed(2)}×MCL` : null)
                                .filter(Boolean)
                                .join(' · ');
                              return (
                                <span title={`${label} v3.2 trajectory — ${tooltip}`} className="hidden sm:inline-block">
                                  <Sparkline data={ratios} width={72} height={18} color={color} showDot showGradient />
                                </span>
                              );
                            };
                            return (
                              <div className="hidden sm:flex flex-col items-end gap-0.5">
                                {renderTraj('BTEX→20yr', cs.btex_trajectory, '#f59e0b')}
                                {renderTraj('TPH→2yr', cs.tph_trajectory, '#06b6d4')}
                              </div>
                            );
                          })()}
                          <div className="flex flex-col items-end">
                            <span
                              className="text-xs px-2 py-0.5 rounded-full inline-block font-bold"
                              style={{ backgroundColor: prioStyle.color + '33', color: prioStyle.color }}
                            >
                              {site.priority_category.toUpperCase()}
                            </span>
                            {site.tier_v32 != null && (
                              <span className="text-[10px] text-mono-500 mt-1 uppercase tracking-wider">
                                T{site.tier_v32} · {site.concern_horizon_label ?? 'clean'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-mono-500 text-sm">
                No sites with predictions
              </div>
            )}
          </div>
        </FadeIn>
      </div>

      {/* Quick Actions */}
      <FadeIn delay={500}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/polygon/sites?priority=critical"
            className="bg-mono-900 border border-mono-700 rounded-lg p-6 hover:border-mono-500 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-mono-100 group-hover:text-mono-50 transition-colors tracking-wider font-orbitron">
                  VIEW CRITICAL
                </h3>
                <p className="text-mono-500 text-xs mt-1">
                  {attentionSites.length} sites requiring attention
                </p>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-mono-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          <Link
            to="/polygon/sites"
            className="bg-mono-900 border border-mono-800 rounded-lg p-6 hover:border-mono-600 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-mono-100 group-hover:text-mono-50 transition-colors tracking-wider font-orbitron">
                  ALL SITES
                </h3>
                <p className="text-mono-500 text-xs mt-1">
                  View all {portfolio.total_sites} sites with predictions
                </p>
              </div>
              <MapPinIcon className="h-5 w-5 text-mono-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          <Link
            to="/polygon/upload"
            className="bg-mono-900 border border-mono-800 rounded-lg p-6 hover:border-mono-600 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-mono-100 group-hover:text-mono-50 transition-colors tracking-wider font-orbitron">
                  ADD NEW SITE
                </h3>
                <p className="text-mono-500 text-xs mt-1">
                  Upload site data for analysis
                </p>
              </div>
              <CloudArrowUpIcon className="h-5 w-5 text-mono-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </FadeIn>
    </div>
  );
}
